"use client";

import {
  buildPimaEscalationActionText,
  getPimaAppointmentReason,
} from "@/configuration/groups/ushv-pima/referral-scheduling-text";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { ReferralSchedulingRecommendation } from "@/src/components/comms/referral-scheduling-recommendation";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { useReferralSchedulingRecommendation } from "@/src/lib/data-fetchers/scheduling-fetchers";
import { isStatOrUrgentPriority, Referral, SchedulingPriority } from "@/src/models/documents/referral";
import { getFullAddress } from "@/src/models/patient";
import { getProviderNameWithCredentials } from "@/src/models/practitioner";
import { AgenticRecommendation } from "@/src/models/scheduling-recommendation";

function getEscalationReasonText(agenticRecommendation: AgenticRecommendation | null | undefined): string {
  const receivingProviderEvaluation = agenticRecommendation?.evaluatedPractitioners.find((p) => p.isReceivingProvider);

  return receivingProviderEvaluation?.reasonCodes?.[0] ?? "N/A";
}

function getPrimaryDiagnosisText(referral: Referral): string | null {
  // PIMA composes reasonForReferral at intake (clinical-gated reason + all ICD descriptions) and ops
  // reviews/approves it, so the Appointment Details box reads that approved field verbatim.
  return referral.reasonForReferral?.trim() || null;
}

function getActionPriorityText(referral: Referral): string {
  const priority = referral.schedulingPriority || SchedulingPriority.ROUTINE;

  return isStatOrUrgentPriority(priority) ? "High" : "Normal";
}

function PimaReferralInstructionsContent({
  referral,
  schedulingVerificationTaskEnabled,
}: {
  referral: Referral;
  schedulingVerificationTaskEnabled: boolean;
}) {
  const referringProviderName = getProviderNameWithCredentials(referral.referringProvider);
  const receivingProviderName = getProviderNameWithCredentials(referral.receivingProvider);
  const diagnosis = getPrimaryDiagnosisText(referral);
  const assignedTo = "CSO,PIMA Referral Management";
  const priority = getActionPriorityText(referral);
  const patientAddress = getFullAddress(referral.patient);
  const { recommendation, isPending } = useReferralSchedulingRecommendation(referral.id, true /* schedulingEnabled */);

  const appointmentReason = isPending
    ? "Loading…"
    : getPimaAppointmentReason(referral, recommendation?.chartEligibilityCheck);
  const generalNotes = `Referred by ${referringProviderName} for ${diagnosis || "referral"} - Scheduled via SMS by Valerie Health SMS`;
  const escalationReason = getEscalationReasonText(recommendation?.agenticRecommendation);
  const { subject: actionSubject, note: actionNotes } = buildPimaEscalationActionText(
    escalationReason,
    `Referred to Dr ${receivingProviderName} by Dr ${referringProviderName}.`
  );

  return (
    <div className="flex flex-col gap-4">
      <span className="font-medium text-center">Pima Referral Instructions:</span>
      <div className="text-muted-foreground space-y-4">
        {!schedulingVerificationTaskEnabled && (
          <ReferralSchedulingRecommendation referral={referral} title="Scheduling Recommendation:" />
        )}

        <div className="flex flex-col gap-2">
          <span className="font-semibold text-foreground">Appointment Details:</span>
          <div className="ml-4 space-y-1">
            <div>
              <span className="font-medium">Reason:</span> <CopyableContent text={appointmentReason} />
            </div>
            <div>
              <span className="font-medium">Diagnosis:</span> <CopyableContent text={diagnosis || "N/A"} />
            </div>
            <div>
              <span className="font-medium">Patient Address:</span> <CopyableContent text={patientAddress || "N/A"} />
            </div>
            <div>
              <span className="font-medium">General Notes:</span> <CopyableContent text={generalNotes} />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-semibold text-foreground">Action Details:</span>
          <div className="ml-4 space-y-1">
            <div>
              <span className="font-medium">Subject:</span> <CopyableContent text={actionSubject} />
            </div>
            <div>
              <span className="font-medium">Assigned to:</span> <CopyableContent text={assignedTo} />
            </div>
            <div>
              <span className="font-medium">Due date:</span> <CopyableContent text="Today" />
            </div>
            <div>
              <span className="font-medium">Status:</span> <CopyableContent text="Not Started" />
            </div>
            <div>
              <span className="font-medium">Priority:</span> <CopyableContent text={priority} />
            </div>
            <div>
              <span className="font-medium">Notes:</span> <CopyableContent text={actionNotes} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PimaReferralCards({ referral }: { referral: Referral }) {
  return (
    <>
      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable />}
      {referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable />}
    </>
  );
}

interface PimaReferralInstructionsProps {
  referral: Referral;
  schedulingVerificationTaskEnabled: boolean;
}

export function PimaReferralInstructions({
  referral,
  schedulingVerificationTaskEnabled,
}: PimaReferralInstructionsProps) {
  return (
    <div className="flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto">
      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <PimaReferralInstructionsContent
            referral={referral}
            schedulingVerificationTaskEnabled={schedulingVerificationTaskEnabled}
          />
        </div>
      </EhrInstructionsCard>

      <PimaReferralCards referral={referral} />
    </div>
  );
}
